import React, { useEffect, useState } from 'react';
import { motion, useAnimationControls } from 'framer-motion';

const PipelineAnimation = () => {
  const steps = [
    { name: 'Transform', color: 'bg-purple-500', path: 'L-down' },
    { name: 'Validate', color: 'bg-purple-600', path: 'straight' },
    { name: 'Enrich', color: 'bg-blue-600', path: 'L-up' }
  ];

  const controls = useAnimationControls();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    if (!isMounted) return;
    
    const runAnimation = async () => {
      try {
        await controls.start("reset");
        await controls.start("animate");
        await new Promise(resolve => setTimeout(resolve, 3000));
        if (isMounted) {
          runAnimation();
        }
      } catch (error) {
        console.error("Animation error:", error);
      }
    };
    
    runAnimation();
  }, [isMounted, controls]);

  return (
    <div className="relative w-full overflow-hidden">
      <motion.div 
        className="bg-[#EDE9FE] rounded-xl p-4 sm:p-6 relative h-[250px] flex items-center transform-gpu"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="flex items-center w-full justify-center scale-[0.85] sm:scale-100 origin-center">
          {/* Left side - Vertical stack */}
          <div className="space-y-4 sm:space-y-6">
            {[1, 2, 3].map((index) => (
              <div key={`flow-${index}`} className="flex items-center gap-2 sm:gap-3">
                {/* Source Rectangle */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={controls}
                  variants={{
                    reset: { opacity: 0 },
                    animate: { 
                      opacity: 1,
                      transition: { delay: index * 1.0, duration: 0.3 }
                    }
                  }}
                  className="bg-white rounded-lg p-2 shadow-sm w-16 sm:w-24"
                >
                  <div className="h-1 bg-gray-200 rounded w-3/4 mb-1.5" />
                  <div className="h-1 bg-gray-200 rounded w-1/2" />
                </motion.div>

                {/* Connecting Line */}
                <motion.div
                  className="h-[2px] w-6 sm:w-8 bg-purple-500"
                  initial={{ scaleX: 0 }}
                  animate={controls}
                  variants={{
                    reset: { scaleX: 0 },
                    animate: { 
                      scaleX: 1,
                      transition: { delay: index * 1.0 + 0.2, duration: 0.2 }
                    }
                  }}
                />

                {/* Step Button */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={controls}
                  variants={{
                    reset: { opacity: 0 },
                    animate: { 
                      opacity: 1,
                      transition: { delay: index * 1.0 + 0.3, duration: 0.3 }
                    }
                  }}
                  className={`${steps[index-1].color} text-white px-3 sm:px-4 py-1.5 rounded-lg font-medium text-xs w-16 sm:w-24 text-center`}
                >
                  {steps[index-1].name}
                </motion.div>

                {/* Path to Plus */}
                <motion.div
                  className={steps[index-1].path === 'straight' ? 
                    "h-[2px] w-12 sm:w-16 bg-purple-500" : 
                    "relative flex items-center"
                  }
                  initial={steps[index-1].path === 'straight' ? { scaleX: 0 } : { opacity: 0 }}
                  animate={controls}
                  variants={{
                    reset: steps[index-1].path === 'straight' ? { scaleX: 0 } : { opacity: 0 },
                    animate: { 
                      scaleX: steps[index-1].path === 'straight' ? 1 : undefined,
                      opacity: steps[index-1].path === 'straight' ? undefined : 1,
                      transition: { delay: 3.8, duration: 0.5 }
                    }
                  }}
                >
                  {steps[index-1].path !== 'straight' && (
                    <>
                      <motion.div
                        className="h-[2px] w-12 sm:w-16 bg-purple-500"
                        initial={{ scaleX: 0 }}
                        animate={controls}
                        variants={{
                          reset: { scaleX: 0 },
                          animate: { 
                            scaleX: 1,
                            transition: { delay: 3.8, duration: 0.3 }
                          }
                        }}
                      />
                      <div className="relative w-4 h-full">
                        <motion.div
                          className={`absolute w-[2px] bg-purple-500 ${
                            steps[index-1].path === 'L-down' ? 'top-0 h-[24px]' : 'bottom-0 h-[24px]'
                          }`}
                          initial={{ scaleY: 0 }}
                          animate={controls}
                          variants={{
                            reset: { scaleY: 0 },
                            animate: { 
                              scaleY: 1,
                              transition: { delay: 4.1, duration: 0.3 }
                            }
                          }}
                          style={{
                            transformOrigin: steps[index-1].path === 'L-down' ? 'top' : 'bottom'
                          }}
                        />
                      </div>
                    </>
                  )}
                </motion.div>
              </div>
            ))}
          </div>

          {/* Right side - Aggregation */}
          <div className="flex flex-col items-center justify-center relative">
            <div className="flex items-center gap-4" style={{ marginLeft: '-30px' }}>
              <motion.div
                className="bg-purple-600 rounded-full w-6 h-6 flex items-center justify-center text-white text-sm"
                initial={{ scale: 0 }}
                animate={controls}
                variants={{
                  reset: { scale: 0 },
                  animate: { 
                    scale: [0, 1.2, 1],
                    transition: { 
                      delay: 4.3,
                      duration: 0.5,
                      times: [0, 0.6, 1]
                    }
                  }
                }}
              >
                +
              </motion.div>

              {/* Final Output */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={controls}
                variants={{
                  reset: { opacity: 0, x: 20 },
                  animate: { 
                    opacity: 1,
                    x: 0,
                    transition: { delay: 4.8, duration: 0.5 }
                  }
                }}
                className="bg-white rounded-lg p-2 shadow-sm w-16 sm:w-24"
              >
                <div className="h-1 bg-gray-200 rounded w-full mb-1.5" />
                <div className="h-1 bg-gray-200 rounded w-3/4" />
              </motion.div>
            </div>
          </div>
        </div>

        {/* Processing Indicator */}
        <motion.div
          className="absolute bottom-2 right-2 flex space-x-1"
          animate={{
            opacity: [0.3, 1, 0.3],
          }}
          transition={{
            repeat: Infinity,
            duration: 1.5,
            ease: "linear"
          }}
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-1 h-1 bg-purple-500 rounded-full"
              style={{
                animationDelay: `${i * 0.2}s`
              }}
            />
          ))}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default PipelineAnimation; 